/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { CompoundEntityRef } from '@backstage/catalog-model';
import { ResultHighlight } from '@backstage/plugin-search-common';
import {
  SearchAutocomplete,
  SearchContextProvider,
  useSearch,
} from '@backstage/plugin-search-react';
import { HandbookSearchResultListItem } from '@internal/backstage-plugin-custom-search-ui-extension';
import { useTechDocsReaderPage } from '@backstage/plugin-techdocs-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TechDocsSearch as BackstageTechDocsSearch } from '@backstage/plugin-techdocs';
import { Grid, makeStyles } from '@material-ui/core';
import { useAnalytics } from '@backstage/core-plugin-api';

/**
 * Props for {@link TechDocsSearch}
 *
 * @public
 */
export type TechDocsSearchProps = {
  entityId?: CompoundEntityRef;
  entityTitle?: string;
  debounceTime?: number;
  searchResultUrlMapper?: (url: string) => string;
};

type TechDocsDoc = {
  namespace: string;
  kind: string;
  name: string;
  path: string;
  location: string;
  title: string;
};

type TechDocsSearchResult = {
  type: string;
  document: TechDocsDoc;
  highlight?: ResultHighlight;
  rank: number;
};

const isTechDocsSearchResult = (
  option: any,
): option is TechDocsSearchResult => {
  return option?.document;
};

const useStyles = makeStyles({
  search: {
    width: '100%',
    '@media (min-width: 76.1875em)': {
      width: 'calc(100% - 34.4rem)',
      margin: '0 auto',
    },
    '@media print': {
      display: 'none',
    },
  },
});
const usePrevious = (value: any) => {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
};

const TechDocsSearchBar = (props: TechDocsSearchProps) => {
  const {
    entityId,
    entityTitle,
    debounceTime = 150,
    searchResultUrlMapper,
  } = props;
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { setFilters, term, result } = useSearch();
  const analytics = useAnalytics();
  const [options, setOptions] = useState<any[]>([]);
  const previousResult = usePrevious(result);
  useEffect(() => {
    analytics.captureEvent('search:visited', 'search', {
      attributes: {
        page: 'handbook',
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only send event when term exist, previous result was loading and now is not (meaning that the search just returned).
    if (
      term &&
      (previousResult as any)?.loading === true &&
      result?.loading === false
    ) {
      analytics.captureEvent('search:results', term, {
        attributes: {
          inContentHandbookSearchResults: true,
          numberOfResults: result.value?.results?.length || 0,
          results: JSON.stringify(
            result.value?.results?.map(_result => ({
              rank: _result.rank,
              title: _result.document.title,
              location: _result.document.location,
            })) || [],
          ),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, term]);
  useEffect(() => {
    let mounted = true;

    if (mounted && result.value) {
      // TODO: Change this into getting only subset of search results from the BE in the first place
      // once pagination is implemented for search engines
      // See: https://github.com/backstage/backstage/issues/6062
      const searchResults = result.value.results.slice(0, 10);
      setOptions(searchResults);
    }
    return () => {
      mounted = false;
    };
  }, [result?.loading, result.value]);

  // Update the filter context when the entityId changes, e.g. when the search
  // bar continues to be rendered, navigating between different TechDocs sites.
  const { kind, name, namespace } = entityId ?? {};
  useEffect(() => {
    setFilters(prevFilters => {
      return {
        ...prevFilters,
        kind,
        namespace,
        name,
      };
    });
  }, [kind, namespace, name, setFilters]);

  const handleSelection = (
    _: any,
    selection: TechDocsSearchResult | string | null,
  ) => {
    if (isTechDocsSearchResult(selection)) {
      const { location } = selection.document;
      navigate(
        searchResultUrlMapper ? searchResultUrlMapper(location) : location,
      );
    }
  };
  const handleResultClicked = (event: any, rank: number) => {
    analytics.captureEvent('search:result-clicked', event.location, {
      attributes: {
        ...event,
        title: event.title,
        inContentHandbookSearchResultsClicked: true,
        rank,
      },
    });
  };
  return (
    <SearchAutocomplete
      data-testid="techdocs-search-bar"
      size="small"
      open={open && Boolean(term)}
      getOptionLabel={() => ''}
      filterOptions={x => {
        return x; // This is needed to get renderOption to be called after options change. Bug in material-ui?
      }}
      onClose={() => {
        setOpen(false);
      }}
      onOpen={() => {
        setOpen(true);
      }}
      onChange={handleSelection}
      blurOnSelect
      noOptionsText="No results found"
      value={null}
      options={options}
      renderOption={({ document, highlight, rank }) => (
        <>
          <HandbookSearchResultListItem
            result={document as any}
            lineClamp={3}
            asListItem={false}
            highlight={highlight}
            onClick={event => handleResultClicked(event, rank)}
            incontentSearch
          />
        </>
      )}
      loading={result.loading}
      inputDebounceTime={debounceTime}
      inputPlaceholder={`Search ${entityTitle || entityId?.name} docs`}
      freeSolo={false}
    />
  );
};

/**
 * Component used to render search bar on TechDocs page, scoped to
 *
 * @public
 */
export const TechDocsSearch = () => {
  const classes = useStyles();
  const {
    entityMetadata: { value: entityMetadata },
    entityRef,
  } = useTechDocsReaderPage();

  const initialState = {
    term: '',
    types: ['techdocs'],
    pageCursor: '',
    filters: entityRef,
  };
  // if entity is engineering handbook, use the custom TechDocsSearch
  if (
    entityRef.name === 'principal-engineering-handbook' &&
    entityRef.namespace === 'principalfinancialgroup-emu'
  ) {
    return (
      <Grid className={classes.search} xs="auto" item>
        <SearchContextProvider initialState={initialState}>
          <TechDocsSearchBar
            entityId={entityRef}
            entityTitle={entityMetadata?.metadata?.title}
          />
        </SearchContextProvider>
      </Grid>
    );
  }
  return (
    <Grid className={classes.search} xs="auto" item>
      <BackstageTechDocsSearch
        entityId={entityRef}
        entityTitle={entityMetadata?.metadata?.title}
      />
    </Grid>
  );
};
